import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUpRight, Loader2, Pin, PinOff, RefreshCw, X } from 'lucide-react';

const CLOSE_ANIMATION_MS = 160;

// Width of a pinned drawer; the page is narrowed by the same amount.
export const PINNED_DRAWER_WIDTH = 400;

const DrawerContent = ({
	eyebrow,
	title,
	subtitle,
	refreshing,
	onRefresh,
	toolbar,
	fullViewLabel,
	onOpenFullView,
	footerExtra,
	children,
	dock,
	onClose
}) => {
	const pinned = dock.pinned && dock.canPin;
	return (
		<>
			<header className="crm-qa-drawer__header">
				<div className="crm-qa-drawer__heading">
					<p className="crm-qa-drawer__eyebrow">{eyebrow}</p>
					<h2 className="crm-qa-drawer__title">{title}</h2>
					{subtitle && <p className="crm-qa-drawer__subtitle">{subtitle}</p>}
				</div>
				<div className="crm-qa-drawer__actions">
					{dock.canPin && (
						<button
							type="button"
							className={`crm-qa-icon-button${pinned ? ' crm-qa-icon-button--active' : ''}`}
							onClick={() => dock.onPinnedChange(!pinned)}
							title={pinned ? 'Unpin (show as overlay)' : 'Pin to the side'}
							aria-label={pinned ? 'Unpin' : 'Pin to the side'}
							aria-pressed={pinned}
						>
							{pinned ? <PinOff size={16} /> : <Pin size={16} />}
						</button>
					)}
					<button
						type="button"
						className="crm-qa-icon-button"
						onClick={onRefresh}
						disabled={refreshing}
						title="Refresh"
						aria-label="Refresh"
					>
						{refreshing ? <Loader2 size={16} className="crm-qa-spin" /> : <RefreshCw size={16} />}
					</button>
					<button
						type="button"
						className="crm-qa-icon-button"
						onClick={onClose}
						title="Close"
						aria-label="Close"
					>
						<X size={16} />
					</button>
				</div>
			</header>
			{toolbar && <div className="crm-qa-drawer__toolbar">{toolbar}</div>}

			<div className="crm-qa-drawer__body">{children}</div>

			<footer className="crm-qa-drawer__footer">
				{footerExtra}
				<button type="button" className="crm-qa-primary-button" onClick={onOpenFullView}>
					{fullViewLabel}
					<ArrowUpRight size={14} />
				</button>
			</footer>
		</>
	);
};

/**
 * Right-hand drawer shared by the Mail and Calendar quick views: an overlay by
 * default, or, when pinned (`dock`), a column beside the page, which the
 * toolbar narrows via `html[data-crm-quick-access-pinned]`.
 */
const QuickAccessDrawer = ({ open, onClose, dock, ...content }) => {
	const pinned = dock.pinned && dock.canPin;
	// Stay mounted briefly after closing so the slide-out can play.
	const [mounted, setMounted] = useState(open);
	const panelRef = useRef(null);
	const onCloseRef = useRef(onClose);
	useEffect(() => {
		onCloseRef.current = onClose;
	}, [onClose]);

	useEffect(() => {
		if (open) {
			setMounted(true);
			return undefined;
		}
		const timer = window.setTimeout(() => setMounted(false), CLOSE_ANIMATION_MS);
		return () => window.clearTimeout(timer);
	}, [open]);

	// Only the overlay is modal: it locks page scroll, takes focus and closes on Escape.
	useEffect(() => {
		if (!open || pinned) return undefined;
		const handleKeyDown = (event) => {
			if (event.key === 'Escape') onCloseRef.current();
		};
		document.addEventListener('keydown', handleKeyDown);
		document.body.classList.add('crm-quick-access-open');
		panelRef.current?.focus();
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.body.classList.remove('crm-quick-access-open');
		};
	}, [open, pinned]);

	if (pinned) {
		if (!open) return null;
		return createPortal(
			<aside
				className="crm-qa-drawer crm-qa-drawer--pinned"
				style={{ width: PINNED_DRAWER_WIDTH }}
				aria-label={content.eyebrow}
			>
				<DrawerContent {...content} dock={dock} onClose={onClose} />
			</aside>,
			document.body
		);
	}

	if (!open && !mounted) return null;

	const state = open ? 'open' : 'closed';

	return createPortal(
		<>
			<div className={`crm-qa-overlay crm-qa-overlay--${state}`} onClick={onClose} aria-hidden="true" />
			<aside
				ref={panelRef}
				className={`crm-qa-drawer crm-qa-drawer--${state}`}
				role="dialog"
				aria-modal="true"
				aria-label={content.eyebrow}
				tabIndex={-1}
			>
				<DrawerContent {...content} dock={dock} onClose={onClose} />
			</aside>
		</>,
		document.body
	);
};

export const QuickAccessNotice = ({ title, body, actionLabel, onAction }) => (
	<div className="crm-qa-notice">
		<p className="crm-qa-notice__title">{title}</p>
		<p className="crm-qa-notice__body">{body}</p>
		{actionLabel && onAction && (
			<button type="button" className="crm-qa-secondary-button" onClick={onAction}>
				{actionLabel}
			</button>
		)}
	</div>
);

export const ListSkeleton = ({ rows = 8 }) => (
	<div className="crm-qa-skeleton">
		{Array.from({ length: rows }).map((_, index) => (
			<div key={index} className="crm-qa-skeleton__row" />
		))}
	</div>
);

export default QuickAccessDrawer;
